import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IKpiVessel, TStoppageEvents, TStoppageEventsItem } from '../../../kpiTypes';
import { AgGridReact } from 'ag-grid-react';
import { themeBalham } from 'ag-grid-enterprise';
import { ColDef } from 'ag-grid-community';
import MapLibre from '../../../../../shared/components/maps/map-libre/MapLibre';
import { formatValueAsString } from '../../../../../utils/helpers';
import { useAppDispatch, useAppSelector } from '../../../../../core/rootHooks';
import { AppDispatch, RootState } from '../../../../../core/store';
import { getVesselLastStoppageEventsRequestAction } from '../../../../main-map/slices/vesselLastStoppageEventsSlice';
import { stoppagesLayerStyle } from '../../../../../shared/components/maps/map-libre/mapLayers';
import { Layer, Source } from 'react-map-gl';
import StoppageEventsMapLegend from './StoppageEventsMapLegend';
import LoadingSpinner from '../../../../../shared/components/loading-spinner/LoadingSpinner';
import { TVesselInfo } from '../../../../main-map/mapTypes';
import StoppageEventsChart from './StoppageEventsChart';
import ExpandableContainer from '../../../../../shared/components/expandable-container/ExpandableContainer';
import { findCenterPoint } from '../../../../../utils/mapHelpers';
import { selectFeatureDateRangeAnalysisDateRange } from '../../../../date-range/slices/dateRangeSlice';

export interface IStoppageEventsProps {
	vessel: IKpiVessel;
	vesselStoppageEvents: TStoppageEvents | null;
	isInSanctionArea: boolean;
}

const StoppageEvents: React.FC<IStoppageEventsProps> = ({ ...props }) => {
	const dispatch = useAppDispatch<AppDispatch>();
	const { vesselLastStoppageEventsData } = useAppSelector((state: RootState) => state.vesselLastStoppageEventsSlice);
	const analysisDateRange = useAppSelector(selectFeatureDateRangeAnalysisDateRange);

	const [stoppageEvents, setStoppageEvents] = useState<TVesselInfo>();
	const [filteredStoppageEvents, setFilteredStoppageEvents] = useState<TVesselInfo>();

	const gridColDefs: ColDef<TStoppageEventsItem>[] = [
		{ headerName: 'Stoppage Id', field: 'stoppageId' },
		{ headerName: 'Sanctioned Area', field: 'sanctionArea', hide: !props.isInSanctionArea },
		{ headerName: 'General Sea Area', field: 'generalSeaArea' },
		{
			headerName: 'Latitude',
			field: 'latitude',
			valueFormatter: (params) => formatValueAsString(params.value, { minimumFractionDigits: 0, maximumFractionDigits: 5 }),
		},
		{
			headerName: 'Longitude',
			field: 'longitude',
			valueFormatter: (params) => formatValueAsString(params.value, { minimumFractionDigits: 0, maximumFractionDigits: 5 }),
		},
		{ headerName: 'Port', field: 'portName' },
		{
			headerName: 'Start Time',
			field: 'fromDate',
			valueFormatter: (params) => (params.value ? new Date(params.value).toLocaleString() : ''),
		},
		{
			headerName: 'End Time',
			field: 'toDate',
			valueFormatter: (params) => (params.value ? new Date(params.value).toLocaleString() : ''),
		},
		{
			headerName: 'Time Days',
			field: 'timeDays',
			valueFormatter: (params) => formatValueAsString(params.value, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
		},
		{
			headerName: 'Distance Between Stop',
			field: 'distanceBetweenStop',
			hide: true,
			valueFormatter: (params) => formatValueAsString(params.value, { minimumFractionDigits: 0, maximumFractionDigits: 3 }),
		},
		{ headerName: 'Vessel Activity', field: 'vesselActivity' },
	];

	const rowData =
		props.vesselStoppageEvents && props.vesselStoppageEvents.dataList && props.vesselStoppageEvents.dataList.length > 0
			? props.isInSanctionArea
				? props.vesselStoppageEvents.dataList.filter((s) => s.isInSanctionArea !== null && s.isInSanctionArea === true)
				: props.vesselStoppageEvents.dataList
			: [];

	const parsedData = useMemo(() => {
		const dataList =
			props.vesselStoppageEvents && props.vesselStoppageEvents.dataList && props.vesselStoppageEvents.dataList.length > 0
				? props.isInSanctionArea
					? props.vesselStoppageEvents.dataList.filter((s) => s.isInSanctionArea !== null && s.isInSanctionArea === true)
					: props.vesselStoppageEvents.dataList
				: [];

		return Array.isArray(dataList)
			? dataList.map((item, index) => {
					return {
						...item,
						chartDate: new Date(item.fromDate).getTime(), // Ensure each signalDate is a timestamp
						// Add a unique key by combining the timestamp with an index
						uniqueKey: `${new Date(item.fromDate).toISOString().split('T')[0]}-${index}`,
					};
				})
			: ([] as TStoppageEventsItem[]);
	}, [props.isInSanctionArea, props.vesselStoppageEvents?.dataList]);

	useEffect(() => {
		if (props.vessel) {
			const startDate = analysisDateRange.dateRange!.start.toDate('UTC').toISOString();
			const endDate = analysisDateRange.dateRange!.end.toDate('UTC').toISOString();
			dispatch(getVesselLastStoppageEventsRequestAction({ imoNumber: props.vessel.imoNumber, startDate, endDate }));
		}
	}, [dispatch, props.vessel, analysisDateRange]);

	useEffect(() => {
		if (
			vesselLastStoppageEventsData &&
			vesselLastStoppageEventsData.features &&
			vesselLastStoppageEventsData.features.length > 0
		) {
			const filteredDataList =
				props.isInSanctionArea !== null && props.isInSanctionArea === true
					? vesselLastStoppageEventsData?.features
							.filter((item) => item.properties?.imoNumber === props.vessel.imoNumber)
							.filter((item) => item.properties?.isInSanctionArea === true)
					: vesselLastStoppageEventsData?.features.filter((item) => item.properties?.imoNumber === props.vessel.imoNumber);

			if (filteredDataList && filteredDataList.length > 0) {
				const filteredStoppageEvents: TVesselInfo = {
					type: 'FeatureCollection',
					features: filteredDataList,
				};
				setStoppageEvents(filteredStoppageEvents);
				setFilteredStoppageEvents(filteredStoppageEvents);
			}
		}
	}, [props.isInSanctionArea, vesselLastStoppageEventsData]);

	const onStoppageEventsMapLegendSelectionChange = (key: string) => {
		if (!key) {
			setFilteredStoppageEvents(stoppageEvents);
			return;
		}

		if (stoppageEvents && stoppageEvents.features && stoppageEvents.features.length > 0) {
			const filteredStoppageEvents = {
				type: 'FeatureCollection',
				features: stoppageEvents.features.filter((item) => item.properties?.vesselActivity === key),
			};
			setFilteredStoppageEvents(filteredStoppageEvents);
		}
	};

	const ref = useRef<HTMLDivElement>(null);

	const [panelOffset, setPanelOffset] = useState<{ top: number; left: number }>();

	useEffect(() => {
		const updatePanelSize = () => {
			if (ref.current) {
				const { offsetTop, offsetLeft } = ref.current;
				setPanelOffset({ top: offsetTop, left: offsetLeft });
			}
		};

		// Initial measurement
		updatePanelSize();

		// Update on window resize
		window.addEventListener('resize', updatePanelSize);
		return () => {
			window.removeEventListener('resize', updatePanelSize);
		};
	}, []);

	return (
		<>
			{rowData === null || !rowData || rowData.length === 0 ? (
				'No stoppages'
			) : (
				<div ref={ref} className="flex flex-col gap-4 w-full">
					<div className="flex flex-row gap-4 basis-1/2">
						<div className="flex flex-col w-full basis-1/3 border border-msi-darkgrey-color-300 rounded p-1">
							<StoppageEventsMapLegend onSelectionChange={onStoppageEventsMapLegendSelectionChange} />
							{filteredStoppageEvents && (
								<MapLibre center={findCenterPoint(filteredStoppageEvents)} loadChildrenDirectly>
									<Source id="stoppages" type="geojson" data={filteredStoppageEvents}>
										<Layer {...stoppagesLayerStyle} type="circle" />
									</Source>
								</MapLibre>
							)}
						</div>
						<div className="basis-2/3 w-full border border-msi-darkgrey-color-300 rounded">
							{!props.vesselStoppageEvents || props.vesselStoppageEvents?.loading ? (
								<LoadingSpinner size="md" spinnerClassName="p-2" />
							) : (
								<ExpandableContainer panelOffsets={panelOffset}>
									<StoppageEventsChart data={parsedData} />
								</ExpandableContainer>
							)}
						</div>
					</div>
					<div className="flex flex-col gap-4 basis-1/2 border border-msi-darkgrey-color-300 rounded p-1">
						<ExpandableContainer panelOffsets={panelOffset}>
							<AgGridReact
								key={props.vessel.imoNumber}
								columnDefs={gridColDefs}
								rowData={rowData}
								className="w-full h-full"
								theme={themeBalham}
							/>
						</ExpandableContainer>
					</div>
				</div>
			)}
		</>
	);
};

export default StoppageEvents;
